import {
  MAP_IDS,
  type HostArena,
  type MapId,
  type MatchSettings,
  type RoundArena,
  type ZoneKind,
} from '../types';
import { MAP_PRESETS } from '../data/maps';
import { ZONES } from '../data/zones';
import {
  EFFECTIVE_FRICTION_MAX,
  EFFECTIVE_FRICTION_MIN,
  FRICTION_PRESETS,
  type FrictionPreset,
} from '../data/friction';
import { clamp } from '../physics/vec2';

type MapSettings = Pick<MatchSettings, 'mapMode' | 'mapPool' | 'startMapId'>;

/** The rotation: `mapPool` in canonical MAP_IDS order (falls back to every map if empty). */
export function mapRotation(pool: readonly MapId[]): MapId[] {
  const rotation = MAP_IDS.filter((id) => pool.includes(id));
  return rotation.length > 0 ? rotation : [...MAP_IDS];
}

/**
 * Map for a 1-based round. Deterministic from settings, so reconnects and replays agree.
 * Rotation starts at `startMapId` and walks the pool in canonical order.
 */
export function mapForRound(round: number, s: MapSettings): MapId {
  if (s.mapMode === 'fixed') return s.startMapId;
  const rotation = mapRotation(s.mapPool);
  const start = Math.max(0, rotation.indexOf(s.startMapId));
  const n = rotation.length;
  const offset = Math.max(1, Math.floor(round)) - 1;
  return rotation[(start + offset) % n]!;
}

/** The host-editable arena fields of `s` (drops friction and anything else). */
export function hostArenaOf(s: HostArena): HostArena {
  return {
    arenaShape: s.arenaShape,
    startRadius: s.startRadius,
    graceMs: s.graceMs,
    shrinkIntervalMs: s.shrinkIntervalMs,
    shrinkPct: s.shrinkPct,
    lavaDpsPct: s.lavaDpsPct,
  };
}

/**
 * Slide friction in play: the map's surface friction scaled by the lobby preset, clamped, and
 * rounded to float32 so the server simulates with exactly the value clients decode from state.
 */
export function effectiveFriction(surface: number, preset: FrictionPreset): number {
  const mult = surface * FRICTION_PRESETS[preset].mult;
  return Math.fround(clamp(mult, EFFECTIVE_FRICTION_MIN, EFFECTIVE_FRICTION_MAX));
}

/**
 * Slide friction inside a surface zone of `kind` on a map with `surface` friction: one clamp over
 * surface × zone × preset, rounded to float32. The server syncs the result (`ZoneState`).
 */
export function zoneFriction(kind: ZoneKind, surface: number, preset: FrictionPreset): number {
  return effectiveFriction(surface * ZONES[kind].slideFrictionMult, preset);
}

/**
 * Arena for a round: the map's preset in `rotate` mode, the host's own fields in `fixed` mode.
 * Friction is always the map's surface × the lobby preset, in both modes.
 */
export function resolveRoundArena(round: number, s: MatchSettings): RoundArena {
  const mapId = mapForRound(round, s);
  const preset = MAP_PRESETS[mapId];
  const arena = s.mapMode === 'fixed' ? hostArenaOf(s) : hostArenaOf(preset.arena);
  const frictionMult = effectiveFriction(preset.arena.frictionMult, s.frictionPreset);
  return { ...arena, frictionMult, mapId, skin: preset.skin };
}
